import { Box, Tooltip } from '@mui/material';
import { Checkbox, Dialog, Typography } from '@sm-dashboard/ui-components';
import { useEffect, useState } from 'react';
import { ArchiveMeta } from '@sm-dashboard/types';
import { useAppContext } from '@/context/context';
import { UserPermission } from '@/util/Permission';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { updateArchiveMeta, CurrentArchiveType } from './ArchiveActions';

interface TagsAllowProps {
  open: boolean;
  onClose?: () => void;
  itemId?: number;
  existingTags?: string[];
  item?: ArchiveMeta;
  reloadArchiveMenu?: () => void;
  onSuccess?: () => void;
}

export const checkFolderPermissions = (folderTags: string[] = [], userPermission: UserPermission | null): boolean => {
  if (!folderTags || folderTags.length === 0) return true;
  if (!userPermission) return false;
  if (userPermission.isAdmin) return true;
  return folderTags.some(tag => userPermission.hasTag(tag));
};

const lockTooltipTitle = (tagsAllow: string[], message: string) => (
  <Box sx={{ p: 0.5 }}>
    {message}
    <Box component="span" sx={{ display: 'block', mt: 0.5, textDecoration: 'underline' }}>
      מורשים:
    </Box>
    {tagsAllow.join(', ')}
  </Box>
);

const hasLockAccess = (tagsAllow: string[], userPermission: UserPermission) =>
  checkFolderPermissions(tagsAllow, userPermission) || userPermission?.isAdmin;

export const getLockIcon = (tagsAllow: string[] | undefined, userPermission: UserPermission) => {
  if (!tagsAllow) return null;

  return hasLockAccess(tagsAllow, userPermission) ? (
    <Tooltip title={lockTooltipTitle(tagsAllow, 'תיקייה מוגבלת בהרשאות - יש לך גישה.')}>
      <LockOpenIcon color="disabled" fontSize="small" />
    </Tooltip>
  ) : (
    <Tooltip title={lockTooltipTitle(tagsAllow, 'אין לך גישה לתיקייה זו.')}>
      <LockIcon color="error" fontSize="small" />
    </Tooltip>
  );
};

/** Inline "🔒 מוגבל" label (gallery album cards), with the same permissions tooltip as the lock icon. */
export const getLockLabel = (tagsAllow: string[] | undefined, userPermission: UserPermission) => {
  if (!tagsAllow?.length) return null;
  const hasAccess = hasLockAccess(tagsAllow, userPermission);

  return (
    <Tooltip
      title={lockTooltipTitle(tagsAllow, hasAccess ? 'תיקייה מוגבלת בהרשאות - יש לך גישה.' : 'אין לך גישה לתיקייה זו.')}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.375,
          fontWeight: 500,
          color: hasAccess ? 'primary.main' : 'error.main'
        }}
      >
        <LockIcon sx={{ fontSize: 13 }} />
        מוגבל
      </Box>
    </Tooltip>
  );
};

export const getPermissionsMessage = (tagsAllow: string[] | undefined) => (
  <span>
    לגישה לתיקייה זו {tagsAllow && tagsAllow.length > 1 ? 'נדרשות הרשאות' : 'נדרשת הרשאת'}:
    <strong> {tagsAllow?.join(' ,') || 'אין תגיות זמינות'}</strong>. יש לפנות למנהל המערכת לקבלת הרשאות.
  </span>
);

/** Folder permissions dialog: tag checklist with the dialog's own footer (ביטול / שמור). */
const TagsAllow = ({ open, onClose, itemId, existingTags, item, reloadArchiveMenu, onSuccess }: TagsAllowProps) => {
  const [selectedTagsState, setSelectedTags] = useState<string[]>(existingTags || []);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { setSnackbar, tags } = useAppContext();

  useEffect(() => {
    if (existingTags) {
      setSelectedTags(existingTags);
    }
  }, [existingTags]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  const handleSavePermissions = async () => {
    try {
      setSaving(true);
      await updateArchiveMeta(
        {
          id: itemId,
          tagsAllow: selectedTagsState,
          isEdit: false,
          parent: item?.parent || 0,
          sorting: item?.sorting || 0,
          folderName: item?.folderName || '',
          archiveType: item?.archiveType || ''
        } as CurrentArchiveType,
        () => {
          if (onClose) {
            onClose();
            setSnackbar({
              message: 'ההרשאות עודכנו בהצלחה',
              severity: 'success',
              open: true
            });
          }
          if (reloadArchiveMenu) {
            reloadArchiveMenu();
          }
          if (onSuccess) {
            onSuccess();
          }
        }
      );
    } catch (error) {
      console.error('Error saving permissions:', error);
      setError('אירעה שגיאה בשמירת ההרשאות');
      setSnackbar({
        message: 'אירעה שגיאה בשמירת ההרשאות',
        severity: 'error',
        open: true
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose?.()}
      title="הגדרות הרשאות"
      maxWidth="xs"
      fullWidth
      contentPadding="none"
      cancelAction
      primaryAction={{ label: 'שמור', onClick: handleSavePermissions, loading: saving, disabled: !tags?.length }}
    >
      {error ? (
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body" color="text.secondary">
            {error}
          </Typography>
        </Box>
      ) : tags && tags.length > 0 ? (
        // No side padding on the list: rows run edge to edge (full-width hover), their inner padding
        // lines the checkboxes up with the title and the footer.
        <Box sx={{ maxHeight: '50vh', overflow: 'auto', py: 1 }}>
          {tags.map(item => (
            <Box
              key={item.tag}
              sx={{
                // The checkbox brings its own ~9px padding, so the start is shorter to keep the box aligned with the title.
                paddingInlineStart: 1.75,
                paddingInlineEnd: 3,
                '&:hover': { bgcolor: 'action.hover' },
                '& .MuiFormControlLabel-root': { width: '100%', m: 0 }
              }}
            >
              <Checkbox
                checked={selectedTagsState.includes(item.tag)}
                onChange={() => handleTagToggle(item.tag)}
                label={item.tag}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body" color="text.secondary">
            אין תגיות זמינות
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default TagsAllow;
